/**
 * dashboardPreferences.js
 *
 * Pure resolution of the Manager dashboard's **per-user layout**: which widgets
 * and quick actions are on, in what order, and how dense the KPI grid is.
 *
 * Deliberately React-free and IO-free (the hook that loads/saves it lives in
 * `helpers/useDashboardPreferences.js`), so the whole rule set is assertable
 * from Jest — same split as `helpers/managementDashboardProfile.js`.
 *
 * ## The stored shape (`user.dashboardConfig.mobileManagement`)
 *
 * ```js
 * {
 *   version: 1,
 *   kpiColumns: 2,                                  // 1 | 2 | 3 tiles per row
 *   widgets: [{id: 'kpi.revenue', enabled: true}],  // ORDERED, disabled ones kept
 *   quickActions: ['timeTracker', 'newMail'],       // ORDERED, enabled only
 *   updatedAt: '2026-09-13T08:00:00.000Z',
 * }
 * ```
 *
 * Two decisions worth stating, because they are what make releases painless:
 *
 *  - **Disabled widgets are stored, not dropped.** A user who turns a tile off
 *    and back on gets it back in the same place; if we stored only the enabled
 *    ids, re-enabling would always append to the bottom.
 *  - **Unknown ids are dropped on READ, never on write.** A layout saved by a
 *    newer build (or one carrying a widget this release removed) still opens —
 *    the reader keeps what it understands and appends what the catalog gained.
 *    Nothing about a saved layout is ever "migrated".
 */

import {
    DASHBOARD_CONFIG_VERSION,
    DASHBOARD_QUICK_ACTIONS,
    DASHBOARD_WIDGETS,
    DEFAULT_KPI_COLUMNS,
    DEFAULT_LAYOUT_BY_PROFILE,
    KPI_COLUMN_OPTIONS,
    MAX_QUICK_ACTIONS,
    getQuickActionDefinition,
    getWidgetDefinition,
    isWidgetBuildEnabled,
} from '../config/dashboardWidgets';

/**
 * @typedef {object} DashboardWidgetPreference
 * @property {string} id       Widget id (a {@link module:config/dashboardWidgets} catalog id).
 * @property {boolean} enabled Whether the user wants it rendered.
 */

/**
 * @typedef {object} DashboardPreferences
 * @property {number} version                     Schema version of the stored entry.
 * @property {number} kpiColumns                  KPI tiles per row (1 | 2 | 3).
 * @property {DashboardWidgetPreference[]} widgets Ordered widget preferences (disabled kept).
 * @property {string[]} quickActions              Ordered pinned quick-action ids.
 * @property {?string} updatedAt                  ISO timestamp of the last save, `null` for defaults.
 */

/** Coerce to an array without throwing on a malformed stored value. */
const safeArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Clamp a KPI density to a value the grid can actually render.
 *
 * @param {*} value Candidate column count (possibly a string from an old save).
 * @returns {number} One of {@link KPI_COLUMN_OPTIONS}.
 */
export const normalizeKpiColumns = (value) => {
    const columns = Number(value);

    return KPI_COLUMN_OPTIONS.includes(columns) ? columns : DEFAULT_KPI_COLUMNS;
};

/**
 * Build the starting layout for a dashboard profile.
 *
 * The profile list decides order + "on"; every other catalog widget follows in
 * catalog order carrying its own `defaultEnabled`, so a store whose vertical
 * has no preset entry still gets a sensible dashboard instead of an empty one.
 *
 * @param {string} [profile] Profile key from `resolveDashboardProfile`.
 * @returns {DashboardPreferences} A fresh, unsaved preference object.
 */
export const buildDefaultPreferences = (profile) => {
    const preset = DEFAULT_LAYOUT_BY_PROFILE[profile] || DEFAULT_LAYOUT_BY_PROFILE.hospitality;
    const presetIds = safeArray(preset.widgets).filter((id) => !!getWidgetDefinition(id));
    const seen = new Set(presetIds);
    const widgets = [
        ...presetIds.map((id) => ({id, enabled: true})),
        ...DASHBOARD_WIDGETS
            .filter((widget) => !seen.has(widget.id))
            .map((widget) => ({id: widget.id, enabled: !!widget.defaultEnabled})),
    ];
    const presetActions = safeArray(preset.quickActions).filter((id) => !!getQuickActionDefinition(id));
    const quickActions = presetActions.length
        ? presetActions
        : DASHBOARD_QUICK_ACTIONS.filter((action) => action.defaultEnabled).map((action) => action.id);

    return {
        version: DASHBOARD_CONFIG_VERSION,
        kpiColumns: DEFAULT_KPI_COLUMNS,
        widgets,
        quickActions: quickActions.slice(0, MAX_QUICK_ACTIONS),
        updatedAt: null,
    };
};

/**
 * Normalize a stored (or partially stored) layout into something renderable.
 *
 * Rules, in order:
 *  1. A missing/malformed entry falls back to the profile defaults entirely.
 *  2. Saved ids the catalog no longer knows are dropped (a removed widget).
 *  3. Duplicate ids collapse to their first occurrence.
 *  4. Catalog widgets the save predates are APPENDED in catalog order with
 *     their `defaultEnabled` — so a release that adds a widget neither hides it
 *     from existing users nor reshuffles the dashboard they built.
 *
 * @param {object|null|undefined} stored Raw `dashboardConfig.mobileManagement` entry.
 * @param {string} [profile] Profile key used for the fallback defaults.
 * @returns {DashboardPreferences} Always a complete, renderable preference object.
 */
export const normalizeDashboardPreferences = (stored, profile) => {
    const defaults = buildDefaultPreferences(profile);

    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
        return defaults;
    }

    const savedWidgets = safeArray(stored.widgets);
    const seen = new Set();
    const widgets = [];

    savedWidgets.forEach((entry) => {
        const id = typeof entry === 'string' ? entry : entry?.id;

        if (!id || seen.has(id) || !getWidgetDefinition(id)) {
            return;
        }

        seen.add(id);
        widgets.push({
            id,
            // A bare id (older/hand-written save) means "on"; an object carries
            // the explicit flag.
            enabled: typeof entry === 'string' ? true : entry?.enabled !== false,
        });
    });

    DASHBOARD_WIDGETS.forEach((widget) => {
        if (!seen.has(widget.id)) {
            widgets.push({id: widget.id, enabled: !!widget.defaultEnabled});
        }
    });

    const quickSeen = new Set();
    const quickActions = safeArray(stored.quickActions).filter((id) => {
        if (typeof id !== 'string' || quickSeen.has(id) || !getQuickActionDefinition(id)) {
            return false;
        }

        quickSeen.add(id);

        return true;
    });

    return {
        version: DASHBOARD_CONFIG_VERSION,
        kpiColumns: normalizeKpiColumns(stored.kpiColumns),
        widgets: widgets.length ? widgets : defaults.widgets,
        // An empty array is a legitimate choice ("I want no quick actions"), so
        // it must NOT fall back to the defaults — only a missing key does.
        quickActions: Array.isArray(stored.quickActions) ? quickActions.slice(0, MAX_QUICK_ACTIONS) : defaults.quickActions,
        updatedAt: typeof stored.updatedAt === 'string' ? stored.updatedAt : null,
    };
};

/**
 * Strip a preference object down to what gets persisted.
 *
 * Keeps the payload to the ~1 KB the backend's per-namespace cap expects, and
 * keeps the stored shape stable regardless of what the in-memory object grew.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {string} [updatedAt] ISO timestamp to stamp (defaults to now).
 * @returns {object} The `dashboardConfig.mobileManagement` entry to save.
 */
export const serializeDashboardPreferences = (preferences, updatedAt) => ({
    version: DASHBOARD_CONFIG_VERSION,
    kpiColumns: normalizeKpiColumns(preferences?.kpiColumns),
    widgets: safeArray(preferences?.widgets)
        .filter((entry) => entry && typeof entry.id === 'string')
        .map((entry) => ({id: entry.id, enabled: entry.enabled !== false})),
    quickActions: safeArray(preferences?.quickActions).filter((id) => typeof id === 'string'),
    updatedAt: updatedAt || new Date().toISOString(),
});

/**
 * Decide whether a catalog entry may be OFFERED to this user at all.
 *
 * Both gates must pass, and they are different questions: `featureKey` is the
 * build-time switchboard ("did this release ship the surface"), `accessKey` is
 * the store licence + role check ("may this user use it"). A widget that fails
 * either is not rendered AND not listed in the picker — the user can never pin
 * a tile that would show a locked or empty card.
 *
 * @param {{accessKey: ?string, featureKey: ?string}} entry Widget or quick-action definition.
 * @param {(key: string) => {allowed: boolean}} canAccess `useManagementAccess().canAccess`.
 * @returns {boolean} Whether the entry is available to this user.
 */
export const isEntryAvailable = (entry, canAccess) => {
    if (!entry || !isWidgetBuildEnabled(entry)) {
        return false;
    }

    if (!entry.accessKey) {
        return true;
    }

    return typeof canAccess === 'function' ? !!canAccess(entry.accessKey)?.allowed : true;
};

/**
 * Resolve the widgets to RENDER: enabled by the user, available to the user,
 * in the user's order, with their catalog definition attached.
 *
 * @param {DashboardPreferences} preferences Normalized preferences.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {import('../config/dashboardWidgets').DashboardWidget[]} Ordered definitions.
 */
export const resolveActiveWidgets = (preferences, canAccess) => safeArray(preferences?.widgets)
    .filter((entry) => entry?.enabled !== false)
    .map((entry) => getWidgetDefinition(entry.id))
    .filter((widget) => isEntryAvailable(widget, canAccess));

/**
 * Resolve the quick actions to RENDER, in the user's pinned order.
 *
 * @param {DashboardPreferences} preferences Normalized preferences.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {import('../config/dashboardWidgets').DashboardQuickAction[]} Ordered definitions.
 */
export const resolveActiveQuickActions = (preferences, canAccess) => safeArray(preferences?.quickActions)
    .map((id) => getQuickActionDefinition(id))
    .filter((action) => isEntryAvailable(action, canAccess))
    .slice(0, MAX_QUICK_ACTIONS);

/**
 * Every widget this user may be OFFERED in the customization screen, paired
 * with their current on/off state and position.
 *
 * @param {DashboardPreferences} preferences Normalized preferences.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {Array<{widget: import('../config/dashboardWidgets').DashboardWidget, enabled: boolean, index: number}>}
 *   Entries in the USER's order (not catalog order), so the picker reflects the dashboard.
 */
export const resolveOfferedWidgets = (preferences, canAccess) => safeArray(preferences?.widgets)
    .map((entry, index) => ({
        widget: getWidgetDefinition(entry?.id),
        enabled: entry?.enabled !== false,
        index,
    }))
    .filter(({widget}) => isEntryAvailable(widget, canAccess));

/**
 * Every quick action this user may be OFFERED, with its pinned state.
 *
 * Catalog order (not pinned order) because this is a *picker*: a stable list
 * the user scans, where toggling an item must not make it jump.
 *
 * @param {DashboardPreferences} preferences Normalized preferences.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {Array<{action: import('../config/dashboardWidgets').DashboardQuickAction, enabled: boolean}>}
 */
export const resolveOfferedQuickActions = (preferences, canAccess) => {
    const pinned = new Set(safeArray(preferences?.quickActions));

    return DASHBOARD_QUICK_ACTIONS
        .filter((action) => isEntryAvailable(action, canAccess))
        .map((action) => ({action, enabled: pinned.has(action.id)}));
};

/**
 * Toggle one widget on/off, preserving its position.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {string} id Widget id to toggle.
 * @returns {DashboardPreferences} A new preference object (never mutated in place).
 */
export const toggleWidget = (preferences, id) => ({
    ...preferences,
    widgets: safeArray(preferences?.widgets).map((entry) => (entry.id === id
        ? {...entry, enabled: entry.enabled === false}
        : entry)),
});

/**
 * Move a widget one slot up or down in the user's order.
 *
 * The move is computed over the *visible* neighbours, not the raw array: a
 * widget the user cannot see (gated away, or belonging to another section of
 * the picker) must not swallow a tap, which is exactly what a naive
 * `index ± 1` swap does when the neighbour is filtered out of the list.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {string} id Widget id to move.
 * @param {-1|1} direction `-1` = up, `1` = down.
 * @param {(key: string) => {allowed: boolean}} [canAccess] Access checker used to pick the visible neighbour.
 * @returns {DashboardPreferences} A new preference object; unchanged when the move is impossible.
 */
export const moveWidget = (preferences, id, direction, canAccess) => {
    const widgets = safeArray(preferences?.widgets);
    const from = widgets.findIndex((entry) => entry?.id === id);

    if (from === -1 || (direction !== 1 && direction !== -1)) {
        return preferences;
    }

    const isVisible = (entry) => isEntryAvailable(getWidgetDefinition(entry?.id), canAccess);
    let to = from + direction;

    while (to >= 0 && to < widgets.length && !isVisible(widgets[to])) {
        to += direction;
    }

    if (to < 0 || to >= widgets.length) {
        return preferences;
    }

    const next = [...widgets];

    [next[from], next[to]] = [next[to], next[from]];

    return {...preferences, widgets: next};
};

/**
 * Pin/unpin a quick action. Pinning appends to the end of the row (so the
 * order reads as "the order I added them"); unpinning removes it.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {string} id Quick-action id.
 * @returns {DashboardPreferences} A new preference object; unchanged when the cap is reached.
 */
export const toggleQuickAction = (preferences, id) => {
    const current = safeArray(preferences?.quickActions);

    if (current.includes(id)) {
        return {...preferences, quickActions: current.filter((entry) => entry !== id)};
    }

    if (current.length >= MAX_QUICK_ACTIONS) {
        return preferences;
    }

    return {...preferences, quickActions: [...current, id]};
};

/**
 * Move a pinned quick action one slot along the row.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {string} id Quick-action id to move.
 * @param {-1|1} direction `-1` = earlier, `1` = later.
 * @returns {DashboardPreferences} A new preference object; unchanged when the move is impossible.
 */
export const moveQuickAction = (preferences, id, direction) => {
    const current = safeArray(preferences?.quickActions);
    const from = current.indexOf(id);
    const to = from + direction;

    if (from === -1 || to < 0 || to >= current.length) {
        return preferences;
    }

    const next = [...current];

    [next[from], next[to]] = [next[to], next[from]];

    return {...preferences, quickActions: next};
};

/**
 * Set the KPI tile density.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {number} columns Requested tiles per row.
 * @returns {DashboardPreferences} A new preference object.
 */
export const setKpiColumns = (preferences, columns) => ({
    ...preferences,
    kpiColumns: normalizeKpiColumns(columns),
});

/**
 * Enable or disable every widget the user can currently see, in one go.
 *
 * Gated-away widgets keep their stored state untouched: "clear all" is a
 * statement about the dashboard the user is looking at, and silently rewriting
 * entries they cannot see would surprise them the day a licence is added.
 *
 * @param {DashboardPreferences} preferences Current preferences.
 * @param {boolean} enabled Target state.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {DashboardPreferences} A new preference object.
 */
export const setAllWidgets = (preferences, enabled, canAccess) => ({
    ...preferences,
    widgets: safeArray(preferences?.widgets).map((entry) => (isEntryAvailable(getWidgetDefinition(entry?.id), canAccess)
        ? {...entry, enabled: !!enabled}
        : entry)),
});

/**
 * Percentage-style summary for the customization screen header.
 *
 * @param {DashboardPreferences} preferences Normalized preferences.
 * @param {(key: string) => {allowed: boolean}} canAccess Access checker.
 * @returns {{enabledWidgets: number, availableWidgets: number, quickActions: number, maxQuickActions: number}}
 */
export const summarizePreferences = (preferences, canAccess) => {
    const offered = resolveOfferedWidgets(preferences, canAccess);

    return {
        enabledWidgets: offered.filter((entry) => entry.enabled).length,
        availableWidgets: offered.length,
        quickActions: resolveActiveQuickActions(preferences, canAccess).length,
        maxQuickActions: MAX_QUICK_ACTIONS,
    };
};

export default {
    buildDefaultPreferences,
    isEntryAvailable,
    moveQuickAction,
    moveWidget,
    normalizeDashboardPreferences,
    normalizeKpiColumns,
    resolveActiveQuickActions,
    resolveActiveWidgets,
    resolveOfferedQuickActions,
    resolveOfferedWidgets,
    serializeDashboardPreferences,
    setAllWidgets,
    setKpiColumns,
    summarizePreferences,
    toggleQuickAction,
    toggleWidget,
};
